import React, { useState, useEffect, useRef, useCallback, lazy, Suspense } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  MapPin, ArrowRight, ArrowUpDown, Calendar, Clock, Users, Briefcase,
  Shield, Tag, Headphones, CreditCard, Car, Bus, ChevronDown,
  X, Menu, LogOut, User, Globe, Navigation, Timer, Plane, Check, Building2,
  Crosshair, Map as MapIcon, Loader2, Phone
} from 'lucide-react';
import { useAuthStore } from '../store/authStore';
import { searchPlaces, reverseGeocode, calculateDistance, api } from '../services/api';
import { useTranslation, LANGUAGE_OPTIONS } from '../translations';
import { MobileAppBanner } from '../components/MobileAppBanner';

const MapPicker = lazy(() => import('../components/MapPicker'));

// Vehicle types - fallback
const fallbackVehicleTypes = [
  { id: 'economy', name: 'Economy', icon: Car, passengers: 4, luggage: 3 },
  { id: 'comfort', name: 'Comfort', icon: Car, passengers: 4, luggage: 3 },
  { id: 'business', name: 'Business', icon: Car, passengers: 3, luggage: 3 },
  { id: 'premium', name: 'Premium', icon: Car, passengers: 3, luggage: 3 },
  { id: 'suv', name: 'SUV', icon: Car, passengers: 6, luggage: 5 },
  { id: 'van', name: 'Van', icon: Bus, passengers: 8, luggage: 8 },
  { id: 'minibus', name: 'Minibus', icon: Bus, passengers: 16, luggage: 16 },
  { id: 'bus', name: 'Bus', icon: Bus, passengers: 50, luggage: 50 },
];

export default function Landing() {
  const navigate = useNavigate();
  const { user, token, language, setLanguage, login, register, verifyEmail, verify2FA, resendCode, logout, loadFromStorage } = useAuthStore();
  const { t, isRTL } = useTranslation(language);

  // Form state
  const [fromQuery, setFromQuery] = useState('');
  const [toQuery, setToQuery] = useState('');
  const [fromLocation, setFromLocation] = useState(null);
  const [toLocation, setToLocation] = useState(null);
  const [fromPredictions, setFromPredictions] = useState([]);
  const [toPredictions, setToPredictions] = useState([]);
  const [showFromDrop, setShowFromDrop] = useState(false);
  const [showToDrop, setShowToDrop] = useState(false);
  const [routeInfo, setRouteInfo] = useState(null);
  const [activeTab, setActiveTab] = useState('transfer');

  // UI state
  const [showAllReviews, setShowAllReviews] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [isLogin, setIsLogin] = useState(true);
  const [authRedirect, setAuthRedirect] = useState(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [authLoading, setAuthLoading] = useState(false);
  const [authError, setAuthError] = useState('');
  const [showLangMenu, setShowLangMenu] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [verificationMode, setVerificationMode] = useState(false);
  const [verificationEmail, setVerificationEmail] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [twoFAMode, setTwoFAMode] = useState(false);
  const [twoFAEmail, setTwoFAEmail] = useState('');
  const [twoFACode, setTwoFACode] = useState('');
  const [twoFAMessage, setTwoFAMessage] = useState('');
  const [phoneVerifyMode, setPhoneVerifyMode] = useState(false);
  const [phoneVerifyCode, setPhoneVerifyCode] = useState('');
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [forgotMode, setForgotMode] = useState(false); // 'email' | 'code' | false
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');

  const searchTimeout = useRef();
  const [showMapPicker, setShowMapPicker] = useState(null); // 'from' | 'to' | null
  const [geoLoading, setGeoLoading] = useState(null); // 'from' | 'to' | null

  // Dynamic vehicle types from CMS
  const [dynamicVehicleTypes, setDynamicVehicleTypes] = useState(null);
  const vehicleTypes = React.useMemo(() => {
    if (!dynamicVehicleTypes) return fallbackVehicleTypes;
    return dynamicVehicleTypes.map(cat => ({
      id: cat.vehicle_id,
      name: cat.name,
      icon: cat.passengers > 8 ? Bus : Car,
      passengers: cat.passengers,
      luggage: cat.luggage,
      image_path: cat.image_path,
      category_id: cat.id,
    }));
  }, [dynamicVehicleTypes]);

  useEffect(() => {
    api.get('/vehicle-categories').then(res => {
      if (res.data && res.data.length > 0) setDynamicVehicleTypes(res.data);
    }).catch(() => {});
  }, []);

  useEffect(() => { loadFromStorage(); }, [loadFromStorage]);

  // Check for pending 2FA from Google login
  useEffect(() => {
    const pending2faEmail = sessionStorage.getItem('pending_2fa_email');
    const pending2faMessage = sessionStorage.getItem('pending_2fa_message');
    if (pending2faEmail) {
      sessionStorage.removeItem('pending_2fa_email');
      sessionStorage.removeItem('pending_2fa_message');
      setTwoFAEmail(pending2faEmail);
      setTwoFAMessage(pending2faMessage || '');
      setTwoFAMode(true);
      setShowAuthModal(true);
      setIsLogin(true);
    }
  }, []);

  // Dynamic page title
  useEffect(() => { document.title = 'Flughafentransfer buchen | Preise vergleichen — 366Transfer'; }, []);
  // Calculate route when both locations set
  useEffect(() => {
    if (fromLocation && toLocation) {
      const info = calculateDistance(fromLocation.lat, fromLocation.lng, toLocation.lat, toLocation.lng);
      setRouteInfo(info);
    } else {
      setRouteInfo(null);
    }
  }, [fromLocation, toLocation]);

  const doSearch = useCallback(async (query, setter, showSetter) => {
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    if (query.length < 3) { setter([]); showSetter(false); return; }
    searchTimeout.current = setTimeout(async () => {
      const results = await searchPlaces(query);
      setter(results);
      showSetter(results.length > 0);
    }, 350);
  }, []);

  const handleFromSearch = (text) => {
    setFromQuery(text);
    setFromLocation(null);
    doSearch(text, setFromPredictions, setShowFromDrop);
  };

  const handleToSearch = (text) => {
    setToQuery(text);
    setToLocation(null);
    doSearch(text, setToPredictions, setShowToDrop);
  };

  const selectPlace = (p, isFrom) => {
    const loc = { address: p.description, lat: p.lat, lng: p.lng, place_id: p.place_id };
    if (isFrom) {
      setFromQuery(p.name + (p.secondary ? ', ' + p.secondary : ''));
      setFromLocation(loc);
      setShowFromDrop(false);
    } else {
      setToQuery(p.name + (p.secondary ? ', ' + p.secondary : ''));
      setToLocation(loc);
      setShowToDrop(false);
    }
  };

  const swapLocations = () => {
    const tq = fromQuery, tl = fromLocation;
    setFromQuery(toQuery); setFromLocation(toLocation);
    setToQuery(tq); setToLocation(tl);
  };

  const handleGeolocation = (field) => {
    if (!navigator.geolocation) {
      alert('Geolocation wird von Ihrem Browser nicht unterstützt');
      return;
    }
    setGeoLoading(field);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude: lat, longitude: lng } = pos.coords;
        try {
          const data = await reverseGeocode(lat, lng);
          const addr = data.address;
          const loc = { address: addr, lat, lng, place_id: data.place_id || `gps_${lat}_${lng}` };
          if (field === 'from') {
            setFromQuery(addr.split(',').slice(0, 2).join(','));
            setFromLocation(loc);
            setShowFromDrop(false);
          } else {
            setToQuery(addr.split(',').slice(0, 2).join(','));
            setToLocation(loc);
            setShowToDrop(false);
          }
        } catch {
          const loc = { address: `${lat.toFixed(5)}, ${lng.toFixed(5)}`, lat, lng, place_id: `gps_${lat}_${lng}` };
          if (field === 'from') { setFromQuery(`${lat.toFixed(5)}, ${lng.toFixed(5)}`); setFromLocation(loc); }
          else { setToQuery(`${lat.toFixed(5)}, ${lng.toFixed(5)}`); setToLocation(loc); }
        }
        setGeoLoading(null);
      },
      () => { alert('Standort konnte nicht ermittelt werden'); setGeoLoading(null); },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const handleMapSelect = (locData) => {
    const loc = { address: locData.address, lat: locData.lat, lng: locData.lng, place_id: locData.place_id };
    if (showMapPicker === 'from') {
      setFromQuery(locData.address.split(',').slice(0, 2).join(','));
      setFromLocation(loc);
    } else {
      setToQuery(locData.address.split(',').slice(0, 2).join(','));
      setToLocation(loc);
    }
    setShowMapPicker(null);
  };

  const handleAuth = async (e) => {
    e.preventDefault();
    setAuthError('');
    if (!email || !password || (!isLogin && !name)) {
      setAuthError('Bitte füllen Sie alle Pflichtfelder aus');
      return;
    }

    // For registration: require phone verification first
    if (!isLogin && phone && !phoneVerified) {
      setAuthLoading(true);
      try {
        await api.post('/auth/send-phone-verification', { phone });
        setPhoneVerifyMode(true);
        setAuthError('');
      } catch (e) {
        setAuthError(e.response?.data?.detail || 'SMS konnte nicht gesendet werden');
      } finally { setAuthLoading(false); }
      return;
    }

    setAuthLoading(true);
    try {
      let result;
      if (isLogin) {
        result = await login(email, password);
      } else {
        result = await register(email, password, name, phone);
      }

      // Admin 2FA required
      if (result?.requires_2fa) {
        setTwoFAMode(true);
        setTwoFAEmail(result.email);
        setTwoFAMessage(result.message);
        setAuthLoading(false);
        return;
      }

      if (result?.requires_verification) {
        setVerificationEmail(result.email);
        setVerificationMode(true);
        setAuthLoading(false);
        return;
      }

      setShowAuthModal(false);
      setEmail(''); setPassword(''); setName(''); setPhone('');
      setVerificationMode(false); setVerificationCode('');
      // Redirect based on context
      if (authRedirect) {
        navigate(authRedirect);
        setAuthRedirect(null);
      } else if (result?.user?.role === 'admin') {
        navigate('/admin');
      } else if (result?.user?.role === 'driver') {
        navigate('/driver/dashboard');
      }
    } catch (err) {
      const detail = err.response?.data?.detail || '';
      if (detail === 'Email not verified') {
        setVerificationEmail(email);
        setVerificationMode(true);
        // Trigger resend
        try { await resendCode(email); } catch {}
      } else {
        setAuthError(detail || 'Ungültige E-Mail oder Passwort');
      }
    } finally { setAuthLoading(false); }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setAuthError('');
    if (!verificationCode || verificationCode.length !== 6) {
      setAuthError('Bitte geben Sie den 6-stelligen Code ein');
      return;
    }
    setAuthLoading(true);
    try {
      const result = await verifyEmail(verificationEmail, verificationCode);
      setShowAuthModal(false);
      setVerificationMode(false);
      setEmail(''); setPassword(''); setName(''); setPhone(''); setVerificationCode('');
      if (authRedirect) {
        navigate(authRedirect);
        setAuthRedirect(null);
      } else if (result?.user?.role === 'admin') {
        navigate('/admin');
      } else if (result?.user?.role === 'driver') {
        navigate('/driver/dashboard');
      }
    } catch (err) {
      setAuthError(err.response?.data?.detail || 'Ungültiger Code');
    } finally { setAuthLoading(false); }
  };

  const handleResend = async () => {
    try {
      await resendCode(verificationEmail);
      setAuthError('');
    } catch {}
  };

  const handle2FAVerify = async (e) => {
    e.preventDefault();
    setAuthError('');
    if (!twoFACode || twoFACode.length !== 6) {
      setAuthError('Bitte geben Sie den 6-stelligen SMS-Code ein');
      return;
    }
    setAuthLoading(true);
    try {
      const result = await verify2FA(twoFAEmail, twoFACode);
      setShowAuthModal(false);
      setTwoFAMode(false);
      setTwoFACode(''); setTwoFAEmail(''); setEmail(''); setPassword('');
      if (result?.user?.role === 'admin') {
        navigate('/admin');
      }
    } catch (err) {
      setAuthError(err.response?.data?.detail || 'Falscher 2FA-Code');
    } finally { setAuthLoading(false); }
  };

  const handlePhoneVerify = async (e) => {
    e.preventDefault();
    setAuthError('');
    if (!phoneVerifyCode || phoneVerifyCode.length !== 6) {
      setAuthError('Bitte geben Sie den 6-stelligen SMS-Code ein');
      return;
    }
    setAuthLoading(true);
    try {
      await api.post('/auth/verify-phone', { phone, code: phoneVerifyCode });
      setPhoneVerified(true);
      setPhoneVerifyMode(false);
      setPhoneVerifyCode('');
      // Now auto-proceed with registration
      const result = await register(email, password, name, phone);
      if (result?.requires_verification) {
        setVerificationEmail(result.email);
        setVerificationMode(true);
      }
    } catch (err) {
      setAuthError(err.response?.data?.detail || 'Falscher SMS-Code');
    } finally { setAuthLoading(false); }
  };

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!email) { setAuthError('Bitte E-Mail-Adresse eingeben'); return; }
    setAuthLoading(true); setAuthError('');
    try {
      await api.post('/auth/forgot-password', { email });
      setForgotMode('code');
    } catch (err) {
      setAuthError(err.response?.data?.detail || 'Fehler beim Senden');
    } finally { setAuthLoading(false); }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!resetCode || !newPassword) { setAuthError('Bitte alle Felder ausfüllen'); return; }
    if (newPassword.length < 6) { setAuthError('Passwort muss mindestens 6 Zeichen lang sein'); return; }
    setAuthLoading(true); setAuthError('');
    try {
      await api.post('/auth/reset-password', { email, code: resetCode, new_password: newPassword });
      setForgotMode(false); setResetCode(''); setNewPassword('');
      setAuthError('');
      setIsLogin(true);
      alert('Passwort erfolgreich zurückgesetzt! Sie können sich jetzt anmelden.');
    } catch (err) {
      setAuthError(err.response?.data?.detail || 'Fehler beim Zurücksetzen');
    } finally { setAuthLoading(false); }
  };

  const openAuthForDriver = () => {
    if (user && token) {
      navigate('/driver/register');
    } else {
      setAuthRedirect('/driver/register');
      setIsLogin(false);
      setShowAuthModal(true);
    }
  };

  const openAuthForCompany = () => {
    if (user && token) {
      navigate('/company/register');
    } else {
      setAuthRedirect('/company/register');
      setIsLogin(false);
      setShowAuthModal(true);
    }
  };

  const handleContinue = () => {
    if (!fromLocation || !toLocation) {
      alert(t('auth_fill_all'));
      return;
    }
    
    // Navigate to booking details with route data
    navigate('/booking', {
      state: {
        fromLocation,
        toLocation,
        routeInfo
      }
    });
  };

  return (
    <div className={`min-h-screen bg-white ${isRTL ? 'rtl' : 'ltr'}`} dir={isRTL ? 'rtl' : 'ltr'} data-testid="landing-page">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-slate-100" data-testid="main-nav">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <a href="/" className="flex items-baseline gap-0.5" data-testid="logo">
              <span className="text-3xl font-extrabold text-orange-500">366</span>
              <span className="text-lg font-semibold text-slate-800">Transfer</span>
            </a>

            {/* Desktop Nav */}
            <div className="hidden md:flex items-center gap-6">
              <button onClick={openAuthForDriver} className="flex items-center gap-1.5 text-sm font-medium text-slate-600 hover:text-orange-500 transition-colors" data-testid="nav-for-drivers">
                <Car size={16} /> {t('nav_for_drivers')}
              </button>
              <button onClick={openAuthForCompany} className="flex items-center gap-1.5 text-sm font-medium text-slate-600 hover:text-orange-500 transition-colors" data-testid="nav-for-companies">
                <Building2 size={16} /> {t('nav_for_companies')}
              </button>
              {user?.role === 'driver' && (
                <a href="/driver/dashboard" className="flex items-center gap-1.5 text-sm font-medium text-slate-600 hover:text-orange-500 transition-colors" data-testid="nav-driver-dashboard">
                  <Briefcase size={16} /> {t('nav_driver_dashboard')}
                </a>
              )}
              {user && (
                <a href="/my-transfers" className="flex items-center gap-1.5 text-sm font-medium text-slate-600 hover:text-orange-500 transition-colors" data-testid="nav-my-transfers">
                  <Navigation size={16} /> {t('nav_my_transfers')}
                </a>
              )}

              {/* Language */}
              <div className="relative">
                <button
                  onClick={() => setShowLangMenu(!showLangMenu)}
                  className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-slate-50 text-sm font-medium text-slate-600 hover:bg-slate-100 transition-colors"
                  data-testid="language-toggle"
                >
                  <Globe size={15} />
                  {LANGUAGE_OPTIONS.find(l => l.code === language)?.flag}
                  <ChevronDown size={14} />
                </button>
                {showLangMenu && (
                  <div className="absolute right-0 mt-1 w-36 bg-white rounded-xl shadow-xl border border-slate-100 py-1 z-50" data-testid="language-dropdown">
                    {LANGUAGE_OPTIONS.map(l => (
                      <button key={l.code} onClick={() => { setLanguage(l.code); setShowLangMenu(false); }}
                        className={`w-full text-left px-4 py-2.5 text-sm hover:bg-slate-50 transition-colors ${language === l.code ? 'text-orange-500 font-semibold' : 'text-slate-600'}`}
                        data-testid={`lang-${l.code}`}>
                        {l.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* User */}
              {user ? (
                <div className="flex items-center gap-3">
                  {user.role === 'admin' && (
                    <a href="/admin" className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-orange-50 text-orange-600 text-sm font-semibold hover:bg-orange-100 transition-colors" data-testid="admin-dashboard-link">
                      <Shield size={15} /> Admin
                    </a>
                  )}
                  <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-slate-50">
                    <div className="w-7 h-7 rounded-full bg-orange-500 flex items-center justify-center text-white text-xs font-bold">
                      {user.name.charAt(0).toUpperCase()}
                    </div>
                    <span className="text-sm font-medium text-slate-700">{user.name}</span>
                  </div>
                  <button onClick={logout} className="p-2 text-slate-400 hover:text-red-500 transition-colors" data-testid="logout-btn">
                    <LogOut size={18} />
                  </button>
                </div>
              ) : (
                <button onClick={() => setShowAuthModal(true)}
                  className="flex items-center gap-2 bg-orange-500 text-white px-5 py-2 rounded-lg text-sm font-semibold hover:bg-orange-600 transition-all shadow-sm hover:shadow-md"
                  data-testid="login-btn">
                  <User size={16} /> {t('nav_login')}
                </button>
              )}
            </div>

            {/* Mobile menu */}
            <button className="md:hidden p-2" onClick={() => setShowMobileMenu(true)} data-testid="mobile-menu-btn">
              <Menu size={24} className="text-slate-700" />
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="bg-slate-900 relative overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 opacity-90" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-20">
          <div className="flex flex-col lg:flex-row gap-10 lg:gap-16 items-start">
            {/* Hero Text */}
            <div className="flex-1 pt-4">
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white leading-tight tracking-tight" data-testid="hero-title">
                {t('landing_hero_title').split(' ').slice(0, 3).join(' ')}{' '}<br />{t('landing_hero_title').split(' ').slice(3).join(' ')}
              </h1>
              <p className="mt-5 text-lg text-slate-300 max-w-lg leading-relaxed">
                {t('landing_hero_subtitle')}
              </p>
              <div className="mt-8 space-y-3">
                {[t('landing_bullet1'), t('landing_bullet2'), t('landing_bullet3')].map(f => (
                  <div key={f} className="flex items-center gap-3">
                    <div className="w-5 h-5 rounded-full bg-emerald-500/20 flex items-center justify-center">
                      <svg className="w-3 h-3 text-emerald-400" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/></svg>
                    </div>
                    <span className="text-slate-300 text-sm">{f}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Booking Card */}
            <div className="w-full lg:w-[440px] bg-white rounded-2xl shadow-2xl p-6" data-testid="booking-form">
              {/* Tabs */}
              <div className="flex gap-2 mb-5">
                <button 
                  onClick={() => setActiveTab('transfer')}
                  className={`flex items-center gap-1.5 px-4 py-2.5 rounded-lg text-sm font-semibold transition-colors ${
                    activeTab === 'transfer' ? 'bg-orange-50 text-orange-500' : 'bg-slate-50 text-slate-500 hover:bg-slate-100'
                  }`} 
                  data-testid="tab-transfer"
                >
                  <ArrowUpDown size={16} /> Transfer
                </button>
                <button 
                  onClick={() => setActiveTab('hourly')}
                  className={`flex items-center gap-1.5 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                    activeTab === 'hourly' ? 'bg-orange-50 text-orange-500' : 'bg-slate-50 text-slate-500 hover:bg-slate-100'
                  }`} 
                  data-testid="tab-hourly"
                >
                  <Clock size={16} /> {t('landing_tab_hourly')}
                </button>
              </div>

              {/* From */}
              <div className="relative mb-3" data-testid="from-input-wrapper">
                <div className="flex items-center gap-3 bg-slate-50 rounded-xl px-4 py-3 border border-slate-200 focus-within:border-orange-500 focus-within:ring-2 focus-within:ring-orange-500/10 transition-all">
                  <div className="w-3 h-3 rounded-full bg-orange-500 shrink-0" />
                  <input
                    type="text"
                    placeholder={t('landing_from')}
                    value={fromQuery}
                    onChange={e => handleFromSearch(e.target.value)}
                    onFocus={() => { if (fromPredictions.length > 0) setShowFromDrop(true); else if (!fromQuery) setShowFromDrop(true); }}
                    className="flex-1 bg-transparent outline-none text-sm text-slate-800 placeholder-slate-400"
                    data-testid="from-input"
                  />
                  {fromQuery && (
                    <button onClick={() => { setFromQuery(''); setFromLocation(null); setFromPredictions([]); setShowFromDrop(false); }} className="text-slate-300 hover:text-slate-500">
                      <X size={16} />
                    </button>
                  )}
                </div>
                {showFromDrop && (
                  <div className="absolute left-0 right-0 top-full mt-1 bg-white rounded-xl shadow-xl border border-slate-100 z-50 max-h-72 overflow-y-auto" data-testid="from-dropdown">
                    {/* Quick Actions */}
                    <div className="border-b border-slate-100">
                      <button onClick={() => { handleGeolocation('from'); setShowFromDrop(false); }}
                        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-slate-50 text-left transition-colors"
                        data-testid="from-gps-btn">
                        {geoLoading === 'from' ? <Loader2 size={16} className="text-blue-500 animate-spin shrink-0" /> : <Crosshair size={16} className="text-blue-500 shrink-0" />}
                        <div>
                          <div className="text-sm font-medium text-blue-600">Aktueller Standort</div>
                          <div className="text-xs text-slate-400">GPS-Position verwenden</div>
                        </div>
                      </button>
                      <button onClick={() => { setShowFromDrop(false); setShowMapPicker('from'); }}
                        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-slate-50 text-left transition-colors"
                        data-testid="from-map-btn">
                        <MapIcon size={16} className="text-emerald-500 shrink-0" />
                        <div>
                          <div className="text-sm font-medium text-emerald-600">Auf Karte wählen</div>
                          <div className="text-xs text-slate-400">Punkt auf der Karte setzen</div>
                        </div>
                      </button>
                    </div>
                    {/* Search Results */}
                    {fromPredictions.length > 0 && fromPredictions.slice(0, 6).map(p => (
                      <button key={p.place_id} onClick={() => selectPlace(p, true)}
                        className="w-full flex items-start gap-3 px-4 py-3 hover:bg-slate-50 text-left transition-colors border-b border-slate-50 last:border-0">
                        <MapPin size={16} className="text-slate-400 mt-0.5 shrink-0" />
                        <div>
                          <div className="text-sm font-medium text-slate-800">{p.name}</div>
                          <div className="text-xs text-slate-400 mt-0.5">{p.secondary}</div>
                        </div>
                      </button>
                    ))}
                    {fromPredictions.length === 0 && fromQuery.length >= 3 && (
                      <div className="px-4 py-4 text-center text-sm text-slate-400">Keine Ergebnisse gefunden</div>
                    )}
                    {!fromQuery && (
                      <div className="px-4 py-3 text-xs text-slate-400">Tippen Sie einen Ort oder eine Adresse ein...</div>
                    )}
                  </div>
                )}
              </div>

              {/* Swap */}
              <div className="flex justify-center -my-1.5 relative z-10">
                <button onClick={swapLocations} className="w-8 h-8 rounded-full bg-white border-2 border-slate-200 flex items-center justify-center hover:border-orange-500 hover:text-orange-500 transition-colors text-slate-400" data-testid="swap-btn">
                  <ArrowUpDown size={14} />
                </button>
              </div>

              {/* To */}
              <div className="relative mb-3" data-testid="to-input-wrapper">
                <div className="flex items-center gap-3 bg-slate-50 rounded-xl px-4 py-3 border border-slate-200 focus-within:border-orange-500 focus-within:ring-2 focus-within:ring-orange-500/10 transition-all">
                  <div className="w-3 h-3 rounded-full bg-emerald-500 shrink-0" />
                  <input
                    type="text"
                    placeholder={t('landing_to')}
                    value={toQuery}
                    onChange={e => handleToSearch(e.target.value)}
                    onFocus={() => { if (toPredictions.length > 0) setShowToDrop(true); else if (!toQuery) setShowToDrop(true); }}
                    className="flex-1 bg-transparent outline-none text-sm text-slate-800 placeholder-slate-400"
                    data-testid="to-input"
                  />
                  {toQuery && (
                    <button onClick={() => { setToQuery(''); setToLocation(null); setToPredictions([]); setShowToDrop(false); }} className="text-slate-300 hover:text-slate-500">
                      <X size={16} />
                    </button>
                  )}
                </div>
                {showToDrop && (
                  <div className="absolute left-0 right-0 top-full mt-1 bg-white rounded-xl shadow-xl border border-slate-100 z-50 max-h-72 overflow-y-auto" data-testid="to-dropdown">
                    {/* Quick Actions */}
                    <div className="border-b border-slate-100">
                      <button onClick={() => { handleGeolocation('to'); setShowToDrop(false); }}
                        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-slate-50 text-left transition-colors"
                        data-testid="to-gps-btn">
                        {geoLoading === 'to' ? <Loader2 size={16} className="text-blue-500 animate-spin shrink-0" /> : <Crosshair size={16} className="text-blue-500 shrink-0" />}
                        <div>
                          <div className="text-sm font-medium text-blue-600">Aktueller Standort</div>
                          <div className="text-xs text-slate-400">GPS-Position verwenden</div>
                        </div>
                      </button>
                      <button onClick={() => { setShowToDrop(false); setShowMapPicker('to'); }}
                        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-slate-50 text-left transition-colors"
                        data-testid="to-map-btn">
                        <MapIcon size={16} className="text-emerald-500 shrink-0" />
                        <div>
                          <div className="text-sm font-medium text-emerald-600">Auf Karte wählen</div>
                          <div className="text-xs text-slate-400">Punkt auf der Karte setzen</div>
                        </div>
                      </button>
                    </div>
                    {/* Search Results */}
                    {toPredictions.length > 0 && toPredictions.slice(0, 6).map(p => (
                      <button key={p.place_id} onClick={() => selectPlace(p, false)}
                        className="w-full flex items-start gap-3 px-4 py-3 hover:bg-slate-50 text-left transition-colors border-b border-slate-50 last:border-0">
                        <MapPin size={16} className="text-slate-400 mt-0.5 shrink-0" />
                        <div>
                          <div className="text-sm font-medium text-slate-800">{p.name}</div>
                          <div className="text-xs text-slate-400 mt-0.5">{p.secondary}</div>
                        </div>
                      </button>
                    ))}
                    {toPredictions.length === 0 && toQuery.length >= 3 && (
                      <div className="px-4 py-4 text-center text-sm text-slate-400">Keine Ergebnisse gefunden</div>
                    )}
                    {!toQuery && (
                      <div className="px-4 py-3 text-xs text-slate-400">Tippen Sie einen Ort oder eine Adresse ein...</div>
                    )}
                  </div>
                )}
              </div>

              {/* Route Info */}
              {routeInfo && (
                <div className="flex items-center justify-center gap-4 bg-orange-50 rounded-lg px-4 py-2.5 mb-4" data-testid="route-info">
                  <div className="flex items-center gap-1.5">
                    <Navigation size={14} className="text-orange-500" />
                    <span className="text-sm font-semibold text-orange-800">{routeInfo.distanceText}</span>
                  </div>
                  <div className="w-px h-4 bg-orange-300" />
                  <div className="flex items-center gap-1.5">
                    <Timer size={14} className="text-orange-500" />
                    <span className="text-sm font-semibold text-orange-800">~{routeInfo.durationText}</span>
                  </div>
                </div>
              )}

              {/* Continue Button */}
              <button 
                onClick={handleContinue}
                disabled={!fromLocation || !toLocation}
                className="w-full bg-emerald-500 hover:bg-emerald-600 text-white py-3.5 rounded-xl text-sm font-bold flex items-center justify-center gap-2 transition-all shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="continue-btn"
              >
                {t('landing_continue')} <ArrowRight size={18} />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Vehicle Types */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16" data-testid="vehicles-section">
        <h2 className="text-2xl sm:text-3xl font-bold text-center text-slate-900">{t('landing_vehicles')}</h2>
        <p className="text-sm text-slate-500 text-center mt-2 mb-10">{t('landing_vehicles_subtitle')}</p>
        <div className="flex gap-4 overflow-x-auto pb-4 scrollbar-hide">
          {vehicleTypes.map(v => {
            const Icon = v.icon;
            return (
              <div key={v.id} className="flex-shrink-0 w-36 flex flex-col items-center p-5 rounded-2xl border-2 border-slate-100 bg-white hover:border-slate-200 transition-all">
                {v.image_path ? (
                  <img
                    src={`${process.env.REACT_APP_BACKEND_URL}/api/vehicle-categories/${v.category_id}/image`}
                    alt={v.name}
                    className="h-10 w-auto object-contain"
                    onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling && (e.target.nextSibling.style.display = 'block'); }}
                  />
                ) : null}
                {!v.image_path && <Icon size={36} className="text-slate-600" />}
                <span className="mt-3 text-sm font-semibold text-slate-700">{v.name}</span>
                <span className="text-xs text-slate-400 mt-1">{v.passengers} Pers. · {Math.max(v.luggage || 0, v.passengers || 0)} Gep.</span>
              </div>
            );
          })}
        </div>
      </section>

      {/* How It Works */}
      <section className="bg-slate-50 py-16" data-testid="how-it-works-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl sm:text-3xl font-bold text-center text-slate-900">{t('landing_how_it_works')}</h2>
          <p className="text-sm text-slate-500 text-center mt-2 mb-12">Buchen Sie Ihren Transfer in 4 einfachen Schritten</p>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { n: '1', t: t('landing_step1_title'), d: t('landing_step1_desc') },
              { n: '2', t: t('landing_step3_title'), d: t('landing_step3_desc') },
              { n: '3', t: t('landing_step2_title'), d: t('landing_step2_desc') },
              { n: '4', t: 'Sicher bezahlen', d: 'Kreditkarte, PayPal, Twint & mehr' },
            ].map(s => (
              <div key={s.n} className="flex flex-col items-center text-center">
                <div className="w-14 h-14 rounded-full bg-orange-500 flex items-center justify-center text-white text-xl font-bold mb-4 shadow-lg shadow-orange-200">
                  {s.n}
                </div>
                <h3 className="font-semibold text-slate-800">{s.t}</h3>
                <p className="text-sm text-slate-500 mt-1 max-w-[160px]">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16" data-testid="features-section">
        <h2 className="text-2xl sm:text-3xl font-bold text-center text-slate-900">{t('landing_why_title')}</h2>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 mt-12">
          {[
            { icon: Shield, t: t('landing_safe'), d: t('landing_safe_desc') },
            { icon: Tag, t: t('landing_best_price'), d: t('landing_best_price_desc') },
            { icon: Headphones, t: t('landing_support'), d: t('landing_support_desc') },
            { icon: CreditCard, t: t('landing_easy_payment'), d: t('landing_easy_payment_desc') },
          ].map(f => {
            const Icon = f.icon;
            return (
              <div key={f.t} className="flex flex-col items-center text-center p-6">
                <div className="w-16 h-16 rounded-2xl bg-orange-50 flex items-center justify-center mb-4">
                  <Icon size={28} className="text-orange-500" />
                </div>
                <h3 className="font-semibold text-slate-800">{f.t}</h3>
                <p className="text-sm text-slate-500 mt-1">{f.d}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Get the App */}
      <section className="bg-slate-900 py-16" data-testid="get-the-app-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            {/* Left: Text + Store badges */}
            <div className="text-center lg:text-left">
              <h2 className="text-3xl sm:text-4xl font-bold text-white">
                {t('landing_get_app_title')}
              </h2>
              <p className="text-slate-400 mt-3 text-base max-w-md mx-auto lg:mx-0">
                {t('landing_get_app_desc')}
              </p>

              <div className="mt-8 flex flex-col sm:flex-row gap-3 items-center lg:items-start justify-center lg:justify-start">
                <a
                  href="https://play.google.com/store/apps/details?id=com.transfer366.app"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-3 bg-black hover:bg-slate-800 transition-colors rounded-xl px-5 py-3 w-full sm:w-auto"
                  data-testid="badge-google-play"
                >
                  <svg viewBox="0 0 512 512" className="w-7 h-7 shrink-0" aria-hidden="true">
                    <path fill="#00D7FE" d="M325.3 234.3L104.3 13.3c-4.4-4.4-10.4-6.9-16.6-6.9-6.2 0-12.2 2.5-16.6 6.9-4.4 4.4-6.9 10.4-6.9 16.6v452c0 6.2 2.5 12.2 6.9 16.6 4.4 4.4 10.4 6.9 16.6 6.9 6.2 0 12.2-2.5 16.6-6.9l221-221L325.3 234.3z"/>
                    <path fill="#FFCE00" d="M447.3 240.3l-67-37.3-71.7 71.7 71.7 71.7 67-37.3c8.1-4.5 13.2-13.1 13.2-22.4 0-9.3-5.1-17.9-13.2-22.4z"/>
                    <path fill="#FF3A44" d="M325.3 277.7L104.3 498.7c4.4 4.4 10.4 6.9 16.6 6.9 6.2 0 12.2-2.5 16.6-6.9l242.8-135-54.9-54.9z"/>
                    <path fill="#48FF48" d="M137.5 13.3c-4.4-4.4-10.4-6.9-16.6-6.9-6.2 0-12.2 2.5-16.6 6.9l221 221 54.9-54.9-242.8-166z"/>
                  </svg>
                  <div className="text-left leading-tight">
                    <div className="text-[10px] text-slate-300 uppercase tracking-wide">Jetzt bei</div>
                    <div className="text-base font-semibold text-white -mt-0.5">Google Play</div>
                  </div>
                </a>

                <a
                  href="https://apps.apple.com/app/366transfer"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-3 bg-black hover:bg-slate-800 transition-colors rounded-xl px-5 py-3 w-full sm:w-auto"
                  data-testid="badge-app-store"
                >
                  <svg viewBox="0 0 24 24" className="w-7 h-7 shrink-0 text-white fill-current" aria-hidden="true">
                    <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
                  </svg>
                  <div className="text-left leading-tight">
                    <div className="text-[10px] text-slate-300 uppercase tracking-wide">Laden im</div>
                    <div className="text-base font-semibold text-white -mt-0.5">App Store</div>
                  </div>
                </a>
              </div>

              <p className="text-xs text-slate-500 mt-5">
                {t('landing_get_app_hint')}
              </p>
            </div>

            {/* Right: QR code */}
            <div className="flex items-center justify-center">
              <div className="bg-white p-4 rounded-2xl shadow-2xl">
                <img
                  src="/images/app-qrcode.png"
                  alt="QR-Code zum Download der 366Transfer App"
                  className="w-48 h-48 sm:w-56 sm:h-56 block"
                  data-testid="app-qrcode-img"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Customer Reviews */}
      <section className="bg-slate-50 py-16" data-testid="reviews-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl sm:text-3xl font-bold text-center text-slate-900">Was unsere Kunden sagen</h2>
          <p className="text-sm text-slate-500 text-center mt-2 mb-12">Echte Bewertungen von zufriedenen Kunden</p>
          {(() => {
            const reviews = [
              { name: 'Thomas M.', country: '🇨🇭', route: 'Zürich Flughafen → Luzern', text: 'Hervorragender Service! Der Fahrer war pünktlich, das Auto sauber und die Fahrt sehr angenehm. Preis war deutlich günstiger als ein normales Taxi.', stars: 5, date: 'März 2026' },
              { name: 'Sarah K.', country: '🇩🇪', route: 'Hurghada Flughafen → Marsa Alam', text: 'Perfekt organisiert! Nach einem langen Flug war der Fahrer schon da und hat uns sicher zum Hotel gebracht. Super einfache Buchung über die Plattform.', stars: 5, date: 'Februar 2026' },
              { name: 'Michael B.', country: '🇨🇭', route: 'Basel Flughafen → Freiburg', text: 'Sehr empfehlenswert! Man bekommt mehrere Angebote und kann den besten Preis wählen. Unser Fahrer war freundlich und professionell.', stars: 5, date: 'Januar 2026' },
              { name: 'أحمد حسن', country: '🇪🇬', route: 'مطار الغردقة ← سهل حشيش', text: 'خدمة ممتازة وسائق محترف جدا. السيارة كانت نظيفة والسعر معقول. وصلنا إلى الفندق بدون أي تأخير. أنصح به بشدة.', stars: 5, date: 'مارس 2026', lang: 'ar' },
              { name: 'Julia W.', country: '🇨🇭', route: 'Genf Flughafen → Lausanne', text: 'Alles in allem eine gute Fahrt. Der Fahrer war freundlich, die Kommunikation etwas holprig aber das Fahrzeug war in Ordnung. Gerne wieder.', stars: 4, date: 'März 2026' },
              { name: 'Daniel R.', country: '🇨🇭', route: 'Zürich Flughafen → St. Moritz', text: 'Lange Strecke, aber der Fahrer kannte sich aus und hat uns sicher durch den Schnee gebracht. Gepäck passte trotz Ski problemlos rein. Top!', stars: 5, date: 'Februar 2026' },
              { name: 'فاطمة علي', country: '🇪🇬', route: 'مطار الغردقة ← الجونة', text: 'الحجز كان سهل جدا عبر الموقع. السائق وصل في الموعد تماما وكان لطيفا مع الأطفال. السيارة واسعة ومكيفة بشكل جيد.', stars: 4, date: 'فبراير 2026', lang: 'ar' },
              { name: 'Markus H.', country: '🇩🇪', route: 'Stuttgart → Zürich Flughafen', text: 'Frühmorgens um 4 Uhr war der Fahrer pünktlich vor der Tür. Entspannte Fahrt, habe sogar noch geschlafen. Flug rechtzeitig erreicht.', stars: 5, date: 'Januar 2026' },
              { name: 'Anna L.', country: '🇦🇹', route: 'Hurghada Flughafen → El Gouna', text: 'Nach dem langen Flug war es ein Segen, dass der Fahrer schon wartete. Kaltes Wasser im Auto und eine angenehme Fahrt. Danke!', stars: 5, date: 'Dezember 2025' },
              { name: 'محمد إبراهيم', country: '🇪🇬', route: 'مطار القاهرة ← الإسكندرية', text: 'الخدمة جيدة بشكل عام، لكن السائق تأخر قليلا عن الموعد. السيارة كانت مريحة والسعر مناسب مقارنة بالخيارات الأخرى.', stars: 3, date: 'يناير 2026', lang: 'ar' },
              { name: 'Stefan K.', country: '🇨🇭', route: 'Basel → Zürich Flughafen', text: 'Gute Alternative zum Zug mit viel Gepäck. Fahrer war zuvorkommend, Auto neuwertig. Ein bisschen teurer als erwartet, aber Service stimmt.', stars: 4, date: 'Februar 2026' },
              { name: 'Laura P.', country: '🇩🇪', route: 'Zürich Flughafen → Davos', text: 'Für unseren Skiurlaub die beste Wahl! Skibox auf dem Dach, Winterreifen, alles perfekt. Der Fahrer gab uns sogar Tipps für Restaurants.', stars: 5, date: 'Februar 2026' },
              { name: 'خالد محمود', country: '🇪🇬', route: 'مطار شرم الشيخ ← دهب', text: 'رحلة رائعة! السائق كان يتحدث الإنجليزية بطلاقة وأعطانا معلومات مفيدة عن المنطقة. السيارة حديثة ونظيفة. سأستخدم الخدمة مرة أخرى.', stars: 5, date: 'مارس 2026', lang: 'ar' },
              { name: 'Hans-Peter M.', country: '🇨🇭', route: 'Zürich Flughafen → Interlaken', text: 'Professioneller Service. Fahrer hielt auf Wunsch kurz an einem Aussichtspunkt – sehr sympathisch. Auto war eine komfortable Mercedes E-Klasse.', stars: 5, date: 'Januar 2026' },
              { name: 'Natalie F.', country: '🇫🇷', route: 'Genf → Annecy', text: 'Bonne expérience, chauffeur ponctuel et courtois. Le véhicule était propre et confortable. Prix correct pour la distance.', stars: 4, date: 'Februar 2026' },
              { name: 'سارة عبد الله', country: '🇪🇬', route: 'مطار الغردقة ← مكادي باي', text: 'أفضل خدمة نقل جربتها في مصر! السائق ودود جدا ويعرف الطريق تماما. وصلنا بأمان وراحة. شكرا لكم.', stars: 5, date: 'Januar 2026', lang: 'ar' },
              { name: 'Peter S.', country: '🇨🇭', route: 'Zürich Flughafen → Zermatt', text: 'Lange Bergfahrt bei schlechtem Wetter. Fahrer sehr erfahren, hat uns sicher ans Ziel gebracht. Fahrzeug sauber und warm. Empfehlung!', stars: 5, date: 'März 2026' },
              { name: 'يوسف حسين', country: '🇪🇬', route: 'مطار الغردقة ← سفاجا', text: 'التعامل كان محترما والسعر شفاف منذ البداية بدون أي مفاجآت. السيارة كانت جيدة ولكنها ليست جديدة جدا. خدمة موثوقة.', stars: 4, date: 'Dezember 2025', lang: 'ar' },
              { name: 'Monika W.', country: '🇩🇪', route: 'Basel → Grindelwald', text: 'Als Alleinreisende fühlte ich mich sehr sicher. Der Fahrer hat mich direkt bis vor die Haustür des Hotels gebracht. Toller Service!', stars: 5, date: 'Februar 2026' },
              { name: 'Kai B.', country: '🇩🇪', route: 'Hamburg → Zürich Flughafen', text: 'Gute Fahrt, allerdings etwas Verkehr auf der Autobahn. Fahrer hat den Zeitpuffer von Anfang an großzügig geplant – wir waren rechtzeitig am Flughafen.', stars: 4, date: 'Januar 2026' },
              { name: 'محمود السيد', country: '🇪🇬', route: 'مطار الأقصر ← الغردقة', text: 'رحلة طويلة ولكن مريحة. السائق توقف مرتين بناء على طلبنا واستراحنا. أسعار معقولة جدا مقارنة بسيارات الأجرة العادية.', stars: 5, date: 'März 2026', lang: 'ar' },
              { name: 'Claudia B.', country: '🇨🇭', route: 'Bern → Zürich Flughafen', text: 'Top-Service! Habe über die App schnell drei Angebote bekommen und mich für den günstigsten entschieden. Keine versteckten Kosten. Sehr transparent.', stars: 5, date: 'März 2026' },
            ];
            return (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="reviews-grid">
                  {(showAllReviews ? reviews : reviews.slice(0, 6)).map((review, idx) => (
                    <div key={idx} className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm hover:shadow-md transition-shadow flex flex-col" data-testid={`review-card-${idx}`}>
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-1">
                          {[...Array(5)].map((_, i) => (
                            <svg key={i} className={`w-4 h-4 ${i < review.stars ? 'text-amber-400' : 'text-slate-200'} fill-current`} viewBox="0 0 20 20">
                              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                            </svg>
                          ))}
                        </div>
                        <span className="text-lg" title={review.country}>{review.country}</span>
                      </div>
                      <p
                        className="text-sm text-slate-700 leading-relaxed mb-4 flex-1"
                        dir={review.lang === 'ar' ? 'rtl' : 'ltr'}
                        lang={review.lang || 'de'}
                      >
                        {review.lang === 'ar' ? `"${review.text}"` : `"${review.text}"`}
                      </p>
                      <div className="border-t border-slate-100 pt-3">
                        <p className="text-sm font-semibold text-slate-800" dir={review.lang === 'ar' ? 'rtl' : 'ltr'}>{review.name}</p>
                        <p className="text-xs text-slate-500 flex items-center gap-1 mt-0.5" dir={review.lang === 'ar' ? 'rtl' : 'ltr'}>
                          <MapPin size={12} className="shrink-0" /> <span className="truncate">{review.route}</span>
                        </p>
                        <p className="text-xs text-slate-400 mt-1">{review.date}</p>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="flex justify-center mt-10">
                  <button
                    onClick={() => setShowAllReviews(!showAllReviews)}
                    className="px-6 py-2.5 bg-white border border-slate-300 text-slate-700 rounded-lg font-medium hover:bg-slate-50 hover:border-slate-400 transition-colors"
                    data-testid="toggle-reviews-btn"
                  >
                    {showAllReviews ? 'Weniger anzeigen' : `Alle ${reviews.length} Bewertungen ansehen`}
                  </button>
                </div>
              </>
            );
          })()}
        </div>
      </section>

      {/* Payment Methods */}
      <section className="bg-slate-50 py-10" data-testid="payment-methods-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-center text-sm font-semibold text-slate-500 mb-6">Sichere Zahlungsmethoden</p>
          <div className="flex flex-wrap items-center justify-center gap-8">
            {/* Visa */}
            <div className="flex items-center gap-2 px-4 py-2 bg-white rounded-lg shadow-sm border border-slate-100" data-testid="payment-visa">
              <svg width="40" height="24" viewBox="0 0 40 24" fill="none"><rect width="40" height="24" rx="4" fill="#1A1F71"/><path d="M17.5 16.5h-3l1.875-9h3L17.5 16.5zM26.25 7.8c-.6-.225-1.537-.475-2.712-.475-3 0-5.113 1.5-5.125 3.65-.025 1.587 1.512 2.475 2.662 3.012 1.175.55 1.575.9 1.575 1.388-.012.75-.95 1.1-1.825 1.1-1.212 0-1.862-.175-2.862-.6l-.4-.175-.425 2.475c.712.312 2.025.587 3.387.6 3.188 0 5.263-1.475 5.288-3.775.012-1.262-.8-2.225-2.563-3.012-1.062-.512-1.712-.862-1.712-1.388.012-.475.55-.962 1.75-.962.987-.012 1.712.2 2.262.425l.275.125.412-2.388zM32.5 7.5h-2.35c-.725 0-1.275.2-1.587.925L24.75 16.5h3.188l.637-1.65h3.887l.363 1.65H35.5L32.5 7.5zm-3.537 6.6l1.6-4.125.225-.6.137.362.925 4.363h-2.887zM13.75 7.5l-2.812 6.15-.312-1.5c-.525-1.687-2.175-3.512-4.013-4.425l2.725 9.762h3.212L16.975 7.5H13.75z" fill="#fff"/><path d="M8.75 7.5H3.825L3.8 7.75c3.812.913 6.337 3.125 7.387 5.775L10.05 8.45c-.175-.712-.712-.925-1.3-.95z" fill="#F9A533"/></svg>
            </div>
            {/* Mastercard */}
            <div className="flex items-center gap-2 px-4 py-2 bg-white rounded-lg shadow-sm border border-slate-100" data-testid="payment-mastercard">
              <svg width="40" height="24" viewBox="0 0 40 24"><rect width="40" height="24" rx="4" fill="#252525"/><circle cx="16" cy="12" r="7" fill="#EB001B"/><circle cx="24" cy="12" r="7" fill="#F79E1B"/><path d="M20 6.8A6.97 6.97 0 0 1 23 12a6.97 6.97 0 0 1-3 5.2A6.97 6.97 0 0 1 17 12a6.97 6.97 0 0 1 3-5.2z" fill="#FF5F00"/></svg>
            </div>
            {/* PayPal */}
            <div className="flex items-center gap-2 px-4 py-2 bg-white rounded-lg shadow-sm border border-slate-100" data-testid="payment-paypal">
              <svg width="40" height="24" viewBox="0 0 40 24"><rect width="40" height="24" rx="4" fill="#fff" stroke="#e2e8f0"/><path d="M15.6 6h4.5c2 0 3.5.8 3.2 2.8-.4 2.5-2 3.7-4.2 3.7h-1.2c-.3 0-.5.2-.6.5L17 15.3c0 .2-.2.3-.4.3H14.8c-.2 0-.4-.2-.3-.4L15.6 6z" fill="#003087"/><path d="M17.5 8.5h3c1.5 0 2.5.5 2.3 2-.3 1.8-1.5 2.8-3.2 2.8h-1c-.2 0-.4.2-.4.4l-.3 1.8c0 .2-.2.3-.4.3h-1.4c-.2 0-.3-.2-.3-.3l1.7-7z" fill="#0070E0"/></svg>
            </div>
            {/* Twint */}
            <div className="flex items-center gap-2 px-4 py-2 bg-white rounded-lg shadow-sm border border-slate-100" data-testid="payment-twint">
              <div className="flex items-center gap-1.5">
                <div className="w-6 h-6 bg-black rounded flex items-center justify-center">
                  <span className="text-[8px] font-black text-white tracking-tight">TW</span>
                </div>
                <span className="text-xs font-bold text-slate-700">TWINT</span>
              </div>
            </div>
            {/* Apple Pay */}
            <div className="flex items-center gap-2 px-4 py-2 bg-white rounded-lg shadow-sm border border-slate-100" data-testid="payment-applepay">
              <svg width="40" height="24" viewBox="0 0 40 24"><rect width="40" height="24" rx="4" fill="#000"/><path d="M12.5 8c.5-.6 .8-1.4.7-2.2-.7 0-1.6.5-2.1 1.1-.4.5-.8 1.4-.7 2.1.8.1 1.6-.4 2.1-1zM13.2 9c-1.2-.1-2.1.6-2.7.6-.6 0-1.4-.6-2.3-.6-1.2 0-2.3.7-2.9 1.7-1.2 2.1-.3 5.3.9 7 .6.8 1.3 1.8 2.2 1.7.9 0 1.2-.6 2.3-.6 1.1 0 1.3.6 2.3.6.9 0 1.6-.9 2.2-1.7.7-1 .9-1.9.9-2 0 0-1.8-.7-1.8-2.7 0-1.7 1.4-2.5 1.5-2.6-.8-1.2-2.1-1.4-2.6-1.4z" fill="#fff"/><path d="M20 10h1.5c1 0 1.7.7 1.7 1.7s-.7 1.7-1.8 1.7H21v2h-1V10zm1 2.7h.5c.5 0 .8-.3.8-.8s-.3-.8-.8-.8H21v1.6zM24 14c0-.7.5-1.1 1.4-1.1h.8v-.3c0-.4-.3-.6-.7-.6-.4 0-.7.2-.7.5h-.8c0-.7.6-1.2 1.6-1.2.9 0 1.5.5 1.5 1.2v2.5h-.9v-.6c-.2.4-.7.7-1.2.7-.7 0-1-.4-1-1.1zm2.2-.3v-.3h-.7c-.4 0-.6.2-.6.4 0 .3.2.4.5.4.5 0 .8-.2.8-.5zM28 17l.7-1.8L27 11.5h1l1.2 2.8 1.2-2.8h1L29 17h-1z" fill="#fff"/></svg>
            </div>
            {/* Google Pay */}
            <div className="flex items-center gap-2 px-4 py-2 bg-white rounded-lg shadow-sm border border-slate-100" data-testid="payment-googlepay">
              <svg width="40" height="24" viewBox="0 0 40 24"><rect width="40" height="24" rx="4" fill="#fff" stroke="#e2e8f0"/><path d="M19.5 12.4l.1-.8h-3.7v1.5h2.1c-.1.5-.4.9-.8 1.2v1h1.3c.8-.7 1.2-1.7 1-2.9z" fill="#4285F4"/><path d="M15.9 15.3c1.1 0 2-.4 2.7-1l-1.3-1c-.4.3-.8.4-1.4.4-1.1 0-2-.7-2.3-1.7h-1.4v1c.7 1.4 2.1 2.3 3.7 2.3z" fill="#34A853"/><path d="M13.9 12c-.1-.3-.1-.6 0-.9v-1h-1.4c-.4.7-.4 1.5 0 2.2l1.4-1.3z" fill="#FBBC04"/><path d="M15.9 9.3c.6 0 1.1.2 1.6.6l1.2-1.2c-.8-.7-1.8-1-2.8-1-1.6 0-3 .9-3.7 2.3l1.4 1c.3-1 1.2-1.7 2.3-1.7z" fill="#EA4335"/><text x="23" y="14" fill="#5F6368" fontSize="6.5" fontFamily="Arial" fontWeight="500">Pay</text></svg>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-orange-500" data-testid="cta-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-white">{t('landing_driver_cta_title')}</h2>
          <p className="text-white/80 mt-3 text-base">{t('landing_driver_cta_desc')}</p>
          <button onClick={openAuthForDriver}
            className="inline-flex items-center gap-2 mt-6 bg-white text-orange-500 px-8 py-3.5 rounded-xl text-sm font-bold hover:shadow-lg transition-all cursor-pointer"
            data-testid="cta-register-driver">
            {t('landing_driver_cta_btn')} <ArrowRight size={18} />
          </button>
        </div>
      </section>

      {/* NEW SEO section: Popular Transfer Routes (Egypt-focused, links to /transfer/:from/:to) */}
      <section className="bg-slate-50 py-14" data-testid="popular-routes-section">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-2 text-center">Beliebte Transfer-Routen in Ägypten</h2>
          <p className="text-slate-500 text-center mb-8 text-sm">Verifizierte Fahrer bieten – du wählst das beste Angebot</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
            {[
              // Top-4 Suchvolumen (Airport → Hauptziel)
              { k: 'hurghada-flughafen--marsa-alam',           label: 'Hurghada → Marsa Alam',        km: 280, min: 258, price: 43 },
              { k: 'sharm-el-sheikh-flughafen--dahab',         label: 'Sharm El-Sheikh → Dahab',      km: 85,  min: 78,  price: 26 },
              { k: 'kairo-flughafen--gizeh-pyramiden',         label: 'Kairo → Pyramiden von Gizeh',  km: 45,  min: 42,  price: 22 },
              { k: 'luxor-flughafen--tal-der-koenige',         label: 'Luxor → Tal der Könige',       km: 18,  min: 17,  price: 20 },
              // Top-4 Hotel-Transfers
              { k: 'hurghada-flughafen--el-gouna',             label: 'Hurghada → El Gouna',          km: 30,  min: 28,  price: 21 },
              { k: 'hurghada-flughafen--makadi-bay',           label: 'Hurghada → Makadi Bay',        km: 35,  min: 32,  price: 21 },
              { k: 'hurghada-flughafen--sahl-hasheesh',        label: 'Hurghada → Sahl Hasheesh',     km: 25,  min: 23,  price: 20 },
              { k: 'marsa-alam-flughafen--port-ghalib',        label: 'Marsa Alam → Port Ghalib',     km: 12,  min: 15,  price: 19 },
              // Top-4 Langstrecken (hoher Buchungswert)
              { k: 'kairo-flughafen--hurghada',                label: 'Kairo → Hurghada',             km: 500, min: 462, price: 63 },
              { k: 'kairo-flughafen--alexandria',              label: 'Kairo → Alexandria',           km: 245, min: 226, price: 40 },
              { k: 'hurghada-flughafen--luxor',                label: 'Hurghada → Luxor',             km: 290, min: 268, price: 44 },
              { k: 'sharm-el-sheikh-flughafen--st-katharinenkloster', label: 'Sharm → Katharinenkloster', km: 215, min: 198, price: 37 },
            ].map(rt => {
              const [f, t] = rt.k.split('--');
              return (
                <a key={rt.k} href={`/transfer/${f}/${t}`}
                   className="bg-white rounded-xl border border-slate-100 p-4 hover:border-orange-300 hover:shadow-md hover:-translate-y-0.5 transition-all block"
                   data-testid={`popular-route-card-${rt.k}`}>
                  <p className="font-semibold text-slate-800 text-sm mb-1">{rt.label}</p>
                  <p className="text-xs text-slate-500">{rt.km} km · {Math.floor(rt.min/60) > 0 ? `${Math.floor(rt.min/60)}h ${rt.min%60}m` : `${rt.min} min`}</p>
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-sm text-orange-600 font-bold">Ab €{rt.price}</span>
                    <span className="text-[10px] text-slate-400">8 Fahrzeugtypen</span>
                  </div>
                </a>
              );
            })}
          </div>
          <div className="mt-6 text-center">
            <a href="#booking-widget"
               onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
               className="inline-flex items-center gap-2 px-5 py-2.5 bg-orange-500 hover:bg-orange-600 text-white rounded-lg text-sm font-semibold shadow-sm transition-colors"
               data-testid="popular-routes-cta">
              Deine Route eingeben →
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-slate-400" data-testid="footer">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-10">
            <div>
              <div className="flex items-baseline gap-0.5 mb-3">
                <span className="text-2xl font-extrabold text-orange-500">366</span>
                <span className="text-base font-semibold text-white">Transfer</span>
              </div>
              <p className="text-sm text-slate-500">Ihr vertrauenswürdiger Transferpartner weltweit</p>
              <div className="mt-3 space-y-2">
                <div>
                  <p className="text-xs font-semibold text-slate-400">366Transfer OU</p>
                  <p className="text-xs text-slate-600">Tartu mnt 67/1-13b<br />Tallinn - 10115<br />Estland (EE)</p>
                </div>
                <div>
                  <p className="text-xs text-slate-600">Fahrwangen, AG, Schweiz<br />Marsa Alam, Rotes Meer, Ägypten</p>
                </div>
              </div>
            </div>
            <div>
              <h4 className="text-sm font-semibold text-white mb-4">Services</h4>
              <div className="space-y-2.5 text-sm">
                <a href="/flughafentransfer" className="block hover:text-white transition-colors" data-testid="footer-flughafentransfer">Flughafentransfer Schweiz</a>
                <a href="/flughafentransfer/zuerich" className="block hover:text-white transition-colors" data-testid="footer-flughafentransfer-zuerich">Flughafentransfer Zürich</a>
                <a href="/flughafentransfer/basel" className="block hover:text-white transition-colors" data-testid="footer-flughafentransfer-basel">Flughafentransfer Basel</a>
                <a href="/flughafentransfer/genf" className="block hover:text-white transition-colors" data-testid="footer-flughafentransfer-genf">Flughafentransfer Genf</a>
                <a href="/flughafentransfer/bern" className="block hover:text-white transition-colors" data-testid="footer-flughafentransfer-bern">Flughafentransfer Bern</a>
                <a href="/flughafentransfer-hurghada" className="block hover:text-white transition-colors" data-testid="footer-flughafentransfer-hurghada">🇪🇬 Flughafentransfer Hurghada</a>
                <a href="/flughafentransfer-marsa-alam" className="block hover:text-white transition-colors" data-testid="footer-flughafentransfer-marsa-alam">🇪🇬 Flughafentransfer Marsa Alam</a>
                <a href="/flughafentransfer-luxor" className="block hover:text-white transition-colors" data-testid="footer-flughafentransfer-luxor">🇪🇬 Flughafentransfer Luxor</a>
                <a href="/flughafentransfer-kairo" className="block hover:text-white transition-colors" data-testid="footer-flughafentransfer-kairo">🇪🇬 Flughafentransfer Kairo</a>
                <a href="/flughafentransfer-hurghada-preise" className="block hover:text-white transition-colors" data-testid="footer-taxi-hurghada-preise">🇪🇬 Taxi Hurghada Preise</a>
                <a href="/hurghada" className="block hover:text-white transition-colors" data-testid="footer-batch1-hurghada">🇪🇬 Hurghada Airport Transfer</a>
                <a href="/cairo" className="block hover:text-white transition-colors" data-testid="footer-batch1-cairo">🇪🇬 Cairo Airport Shuttle</a>
                <a href="/sharm" className="block hover:text-white transition-colors" data-testid="footer-batch1-sharm">🇪🇬 Taxi Sharm el Sheikh</a>
                <a href="/safaga" className="block hover:text-white transition-colors" data-testid="footer-batch1-safaga">🇪🇬 Safaga Transfer</a>
                <a href="/private" className="block hover:text-white transition-colors" data-testid="footer-batch1-private">🇪🇬 Private Transfer Ägypten</a>
                <a href="/en/airport-transfer" className="block hover:text-white transition-colors" data-testid="footer-airport-transfer-en">🇬🇧 Airport Transfer (EN)</a>
              </div>
            </div>
            <div>
              <h4 className="text-sm font-semibold text-white mb-4">Unternehmen</h4>
              <div className="space-y-2.5 text-sm">
                <p className="hover:text-white cursor-pointer transition-colors">{t('common_about')}</p>
                <p onClick={openAuthForDriver} className="hover:text-white cursor-pointer transition-colors">{t('nav_for_drivers')}</p>
                <p onClick={openAuthForCompany} className="hover:text-white cursor-pointer transition-colors">{t('nav_for_companies')}</p>
                <a href="/contact" className="block hover:text-white transition-colors" data-testid="footer-contact">{t('common_contact')}</a>
              </div>
            </div>
            <div>
              <h4 className="text-sm font-semibold text-white mb-4">Rechtliches</h4>
              <div className="space-y-2.5 text-sm">
                <a href="/agb" className="block hover:text-white transition-colors" data-testid="footer-agb">{t('footer_terms')}</a>
                <a href="/datenschutz" className="block hover:text-white transition-colors" data-testid="footer-datenschutz">{t('footer_privacy')}</a>
                <a href="/impressum" className="block hover:text-white transition-colors" data-testid="footer-impressum">{t('footer_imprint')}</a>
                <a href="/fahrer-agb" className="block hover:text-white transition-colors" data-testid="footer-fahrer-agb">Fahrer-AGB</a>
              </div>
            </div>
          </div>
          {/* Sprint 3 SEO Footer — 3 additive columns, existing footer untouched */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 mt-10 pt-10 border-t border-white/10" data-testid="footer-seo-block">
            <div>
              <h4 className="text-sm font-semibold text-white mb-4">Flughäfen in Ägypten</h4>
              <div className="space-y-2 text-sm">
                <a href="/transfer/hurghada-flughafen/hurghada-stadt"          className="block hover:text-white transition-colors" data-testid="footer-airport-hurghada">Hurghada</a>
                <a href="/transfer/luxor-flughafen/luxor-stadt"                className="block hover:text-white transition-colors" data-testid="footer-airport-luxor">Luxor</a>
                <a href="/transfer/sharm-el-sheikh-flughafen/naama-bay"        className="block hover:text-white transition-colors" data-testid="footer-airport-sharm-el-sheikh">Sharm El-Sheikh</a>
                <a href="/transfer/kairo-flughafen/kairo-innenstadt"           className="block hover:text-white transition-colors" data-testid="footer-airport-kairo">Kairo</a>
                <a href="/transfer/marsa-alam-flughafen/marsa-alam-stadt"      className="block hover:text-white transition-colors" data-testid="footer-airport-marsa-alam">Marsa Alam</a>
                <a href="/transfer/kairo-flughafen/alexandria"                 className="block hover:text-white transition-colors" data-testid="footer-airport-alexandria">Alexandria</a>
                <a href="/transfer/luxor-flughafen/aswan"                      className="block hover:text-white transition-colors" data-testid="footer-airport-aswan">Aswan</a>
                <a href="/transfer/sharm-el-sheikh-flughafen/taba"             className="block hover:text-white transition-colors" data-testid="footer-airport-taba">Taba</a>
              </div>
            </div>
            <div>
              <h4 className="text-sm font-semibold text-white mb-4">Beliebte Ziele</h4>
              <div className="space-y-2 text-sm">
                <a href="/transfer/hurghada-flughafen/el-quseir"               className="block hover:text-white transition-colors" data-testid="footer-dest-el-quseir">El Quseir</a>
                <a href="/transfer/hurghada-flughafen/marsa-alam"              className="block hover:text-white transition-colors" data-testid="footer-dest-marsa-alam">Marsa Alam</a>
                <a href="/transfer/sharm-el-sheikh-flughafen/dahab"            className="block hover:text-white transition-colors" data-testid="footer-dest-dahab">Dahab</a>
                <a href="/transfer/kairo-flughafen/gizeh-pyramiden"            className="block hover:text-white transition-colors" data-testid="footer-dest-gizeh">Pyramiden von Gizeh</a>
                <a href="/transfer/luxor-flughafen/tal-der-koenige"            className="block hover:text-white transition-colors" data-testid="footer-dest-tal-der-koenige">Tal der Könige</a>
                <a href="/transfer/luxor-flughafen/aswan"                      className="block hover:text-white transition-colors" data-testid="footer-dest-aswan">Aswan</a>
                <a href="/transfer/luxor-flughafen/karnak-tempel"              className="block hover:text-white transition-colors" data-testid="footer-dest-karnak">Karnak-Tempel</a>
                <a href="/transfer/kairo-flughafen/alexandria"                 className="block hover:text-white transition-colors" data-testid="footer-dest-alexandria">Alexandria</a>
                <a href="/transfer/hurghada-flughafen/el-gouna"                className="block hover:text-white transition-colors" data-testid="footer-dest-el-gouna">El Gouna</a>
                <a href="/transfer/sharm-el-sheikh-flughafen/naama-bay"        className="block hover:text-white transition-colors" data-testid="footer-dest-naama-bay">Naama Bay</a>
              </div>
            </div>
            <div>
              <h4 className="text-sm font-semibold text-white mb-4">Top-Transfer-Routen</h4>
              <div className="space-y-2 text-sm">
                <a href="/transfer/hurghada-flughafen/marsa-alam"              className="block hover:text-white transition-colors" data-testid="footer-route-hrg-marsa-alam">Hurghada → Marsa Alam</a>
                <a href="/transfer/hurghada-flughafen/luxor"                   className="block hover:text-white transition-colors" data-testid="footer-route-hrg-luxor">Hurghada → Luxor</a>
                <a href="/transfer/kairo-flughafen/gizeh-pyramiden"            className="block hover:text-white transition-colors" data-testid="footer-route-cai-gizeh">Kairo → Pyramiden von Gizeh</a>
                <a href="/transfer/kairo-flughafen/hurghada"                   className="block hover:text-white transition-colors" data-testid="footer-route-cai-hrg">Kairo → Hurghada</a>
                <a href="/transfer/luxor-flughafen/tal-der-koenige"            className="block hover:text-white transition-colors" data-testid="footer-route-lxr-tal">Luxor → Tal der Könige</a>
                <a href="/transfer/sharm-el-sheikh-flughafen/dahab"            className="block hover:text-white transition-colors" data-testid="footer-route-ssh-dahab">Sharm → Dahab</a>
                <a href="/transfer/marsa-alam-flughafen/port-ghalib"           className="block hover:text-white transition-colors" data-testid="footer-route-rmf-port-ghalib">Marsa Alam → Port Ghalib</a>
                <a href="/transfer/hurghada-flughafen/el-quseir"               className="block hover:text-white transition-colors" data-testid="footer-route-hrg-el-quseir">Hurghada → El Quseir</a>
                <a href="/transfer/kairo-flughafen/alexandria"                 className="block hover:text-white transition-colors" data-testid="footer-route-cai-alexandria">Kairo → Alexandria</a>
                <a href="/transfer/luxor-flughafen/aswan"                      className="block hover:text-white transition-colors" data-testid="footer-route-lxr-aswan">Luxor → Aswan</a>
              </div>
            </div>
          </div>
          <div className="border-t border-white/10 mt-10 pt-6 text-center text-sm text-slate-500">
            © 2026 366Transfer OU. {t('footer_rights')}.
          </div>
        </div>
      </footer>

      {/* Mobile-only floating "Get the App" banner */}
      <MobileAppBanner
        playStoreUrl="https://play.google.com/store/apps/details?id=com.transfer366.app"
        appStoreUrl="https://apps.apple.com/app/366transfer"
      />

      {/* Auth Modal */}
      {showAuthModal && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" onMouseDown={(e) => { if (e.target === e.currentTarget) { setShowAuthModal(false); setVerificationMode(false); } }} data-testid="auth-modal-overlay">
          <div className="bg-white rounded-2xl p-8 w-full max-w-md shadow-2xl" onMouseDown={e => e.stopPropagation()} data-testid="auth-modal">
            <div className="flex justify-between items-start mb-6">
              <div>
                <h2 className="text-2xl font-bold text-slate-900" data-testid="auth-title">
                  {phoneVerifyMode ? 'Mobilnummer bestätigen' : twoFAMode ? '2FA Verifizierung' : verificationMode ? 'E-Mail bestätigen' : forgotMode === 'email' ? 'Passwort vergessen' : forgotMode === 'code' ? 'Neues Passwort' : isLogin ? t('auth_welcome') : t('auth_register_title')}
                </h2>
                <p className="text-sm text-slate-500 mt-1">
                  {phoneVerifyMode
                    ? `SMS-Code an ${phone?.slice(0,6)}***${phone?.slice(-2)} gesendet`
                    : twoFAMode
                    ? twoFAMessage
                    : verificationMode
                    ? `Code wurde an ${verificationEmail} gesendet`
                    : forgotMode === 'email'
                    ? 'Geben Sie Ihre E-Mail-Adresse ein'
                    : forgotMode === 'code'
                    ? `Code wurde an ${email} gesendet`
                    : isLogin ? t('auth_login_subtitle') : t('auth_register_subtitle')}
                </p>
              </div>
              <button type="button" onClick={() => { setShowAuthModal(false); setVerificationMode(false); setTwoFAMode(false); setPhoneVerifyMode(false); setForgotMode(false); setResetCode(''); setNewPassword(''); }} className="text-slate-300 hover:text-slate-500" data-testid="auth-close">
                <X size={20} />
              </button>
            </div>

            {phoneVerifyMode ? (
              <form onSubmit={handlePhoneVerify} className="space-y-3">
                <div className="flex items-center gap-3 bg-blue-50 rounded-xl px-4 py-3 border border-blue-200">
                  <Phone size={18} className="text-blue-500" />
                  <input
                    type="text"
                    placeholder="6-stelliger SMS-Code"
                    value={phoneVerifyCode}
                    onChange={e => setPhoneVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    className="flex-1 bg-transparent outline-none text-sm tracking-widest font-mono text-center text-lg"
                    maxLength={6}
                    autoFocus
                    data-testid="phone-verify-input"
                  />
                </div>
                {authError && (
                  <p className="text-sm text-red-500 text-center" data-testid="auth-error">{authError}</p>
                )}
                <button type="submit" disabled={authLoading || phoneVerifyCode.length !== 6}
                  className="w-full bg-blue-500 hover:bg-blue-600 text-white py-3.5 rounded-xl text-sm font-bold transition-all disabled:opacity-60 mt-2"
                  data-testid="phone-verify-submit">
                  {authLoading ? t('common_loading') : 'Mobilnummer bestätigen'}
                </button>
                <button type="button" onClick={async () => { setAuthError(''); setPhoneVerifyCode(''); try { await api.post('/auth/send-phone-verification', { phone }); } catch {} }}
                  className="block mx-auto text-sm text-blue-500 hover:underline mt-2"
                  data-testid="resend-phone-btn">
                  SMS erneut senden
                </button>
              </form>
            ) : twoFAMode ? (
              <form onSubmit={handle2FAVerify} className="space-y-3">
                <p className="text-xs text-amber-600 bg-amber-50 rounded-lg px-3 py-2 text-center">
                  Code wurde auch per E-Mail gesendet. Bitte prüfen Sie Ihr Postfach.
                </p>
                <div className="flex items-center gap-3 bg-orange-50 rounded-xl px-4 py-3 border border-orange-200">
                  <Phone size={18} className="text-orange-500" />
                  <input
                    type="text"
                    placeholder="6-stelliger Code"
                    value={twoFACode}
                    onChange={e => setTwoFACode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    className="flex-1 bg-transparent outline-none text-sm tracking-widest font-mono text-center text-lg"
                    maxLength={6}
                    autoFocus
                    data-testid="2fa-code-input"
                  />
                </div>
                {authError && (
                  <p className="text-sm text-red-500 text-center" data-testid="auth-error">{authError}</p>
                )}
                <button type="submit" disabled={authLoading || twoFACode.length !== 6}
                  className="w-full bg-orange-500 hover:bg-orange-600 text-white py-3.5 rounded-xl text-sm font-bold transition-all disabled:opacity-60 mt-2"
                  data-testid="2fa-submit">
                  {authLoading ? t('common_loading') : '2FA Code bestätigen'}
                </button>
                <button type="button" onClick={async () => { setAuthError(''); setTwoFACode(''); setTwoFAMode(false); setAuthLoading(true); try { const r = await login(email, password); if (r?.requires_2fa) { setTwoFAMode(true); setTwoFAMessage(r.message); } } catch(e) { setAuthError(e.response?.data?.detail || 'Fehler'); } finally { setAuthLoading(false); } }}
                  className="block mx-auto text-sm text-orange-500 hover:underline mt-2"
                  data-testid="resend-2fa-btn">
                  Code erneut senden
                </button>
              </form>
            ) : verificationMode ? (
              <form onSubmit={handleVerify} className="space-y-3">
                <div className="flex items-center gap-3 bg-slate-50 rounded-xl px-4 py-3 border border-slate-200">
                  <Shield size={18} className="text-slate-400" />
                  <input
                    type="text"
                    placeholder="6-stelliger Code"
                    value={verificationCode}
                    onChange={e => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    className="flex-1 bg-transparent outline-none text-sm tracking-widest font-mono text-center text-lg"
                    maxLength={6}
                    autoFocus
                    data-testid="verification-code-input"
                  />
                </div>

                {authError && (
                  <p className="text-sm text-red-500 text-center" data-testid="auth-error">{authError}</p>
                )}

                <button type="submit" disabled={authLoading || verificationCode.length !== 6}
                  className="w-full bg-orange-500 hover:bg-orange-600 text-white py-3.5 rounded-xl text-sm font-bold transition-all disabled:opacity-60 mt-2"
                  data-testid="verify-submit">
                  {authLoading ? t('common_loading') : 'Code bestätigen'}
                </button>

                <button type="button" onClick={handleResend}
                  className="block mx-auto text-sm text-orange-500 hover:underline mt-2"
                  data-testid="resend-code-btn">
                  Code erneut senden
                </button>
              </form>
            ) : forgotMode ? (
              forgotMode === 'email' ? (
                <form onSubmit={handleForgotPassword} className="space-y-3">
                  <div className="flex items-center gap-3 bg-slate-50 rounded-xl px-4 py-3 border border-slate-200">
                    <svg className="w-[18px] h-[18px] text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
                    <input type="email" placeholder="E-Mail-Adresse" value={email} onChange={e => setEmail(e.target.value)}
                      className="flex-1 bg-transparent outline-none text-sm" autoFocus data-testid="forgot-email" />
                  </div>
                  {authError && <p className="text-sm text-red-500 text-center" data-testid="auth-error">{authError}</p>}
                  <button type="submit" disabled={authLoading}
                    className="w-full bg-orange-500 hover:bg-orange-600 text-white py-3.5 rounded-xl text-sm font-bold transition-all disabled:opacity-60 mt-2"
                    data-testid="forgot-submit">
                    {authLoading ? 'Wird gesendet...' : 'Reset-Code senden'}
                  </button>
                  <button type="button" onClick={() => { setForgotMode(false); setAuthError(''); }}
                    className="block mx-auto text-sm text-orange-500 hover:underline mt-2" data-testid="forgot-back">
                    Zurück zum Login
                  </button>
                </form>
              ) : (
                <form onSubmit={handleResetPassword} className="space-y-3">
                  <div className="flex items-center gap-3 bg-orange-50 rounded-xl px-4 py-3 border border-orange-200">
                    <Shield size={18} className="text-orange-500" />
                    <input type="text" placeholder="6-stelliger Code" value={resetCode}
                      onChange={e => setResetCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      className="flex-1 bg-transparent outline-none text-sm tracking-widest font-mono text-center text-lg"
                      maxLength={6} autoFocus data-testid="reset-code" />
                  </div>
                  <div className="flex items-center gap-3 bg-slate-50 rounded-xl px-4 py-3 border border-slate-200">
                    <svg className="w-[18px] h-[18px] text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>
                    <input type="password" placeholder="Neues Passwort (min. 6 Zeichen)" value={newPassword}
                      onChange={e => setNewPassword(e.target.value)}
                      className="flex-1 bg-transparent outline-none text-sm" data-testid="reset-new-password" />
                  </div>
                  {authError && <p className="text-sm text-red-500 text-center" data-testid="auth-error">{authError}</p>}
                  <button type="submit" disabled={authLoading || resetCode.length !== 6}
                    className="w-full bg-orange-500 hover:bg-orange-600 text-white py-3.5 rounded-xl text-sm font-bold transition-all disabled:opacity-60 mt-2"
                    data-testid="reset-submit">
                    {authLoading ? 'Wird zurückgesetzt...' : 'Passwort zurücksetzen'}
                  </button>
                  <button type="button" onClick={async () => { setAuthError(''); setResetCode(''); try { await api.post('/auth/forgot-password', { email }); } catch {} }}
                    className="block mx-auto text-sm text-orange-500 hover:underline mt-2" data-testid="resend-reset-code">
                    Code erneut senden
                  </button>
                </form>
              )
            ) : (
              <>
                {/* Google Login Button */}
                <button
                  type="button"
                  onClick={handleGoogleLogin}
                  className="w-full flex items-center justify-center gap-3 bg-white border-2 border-slate-200 hover:border-slate-300 py-3 rounded-xl text-sm font-medium text-slate-700 transition-all hover:shadow-sm mb-4"
                  data-testid="google-login-btn"
                >
                  <svg width="18" height="18" viewBox="0 0 18 18"><path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615Z" fill="#4285F4"/><path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18Z" fill="#34A853"/><path d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.997 8.997 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332Z" fill="#FBBC05"/><path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58Z" fill="#EA4335"/></svg>
                  Mit Google anmelden
                </button>

                <div className="flex items-center gap-3 mb-4">
                  <div className="flex-1 h-px bg-slate-200"></div>
                  <span className="text-xs text-slate-400">oder</span>
                  <div className="flex-1 h-px bg-slate-200"></div>
                </div>

                <form onSubmit={handleAuth} className="space-y-3" autoComplete="off">
                  {!isLogin && (
                    <div className="flex items-center gap-3 bg-slate-50 rounded-xl px-4 py-3 border border-slate-200">
                      <User size={18} className="text-slate-400" />
                      <input type="text" placeholder={t('auth_name')} value={name} onChange={e => setName(e.target.value)}
                        className="flex-1 bg-transparent outline-none text-sm" autoComplete="off" data-testid="auth-name" />
                    </div>
                  )}
                  <div className="flex items-center gap-3 bg-slate-50 rounded-xl px-4 py-3 border border-slate-200">
                    <svg className="w-[18px] h-[18px] text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
                    <input type="email" placeholder={t('auth_email')} value={email} onChange={e => setEmail(e.target.value)}
                      className="flex-1 bg-transparent outline-none text-sm" autoComplete="new-email" data-testid="auth-email" />
                  </div>
                  <div className="flex items-center gap-3 bg-slate-50 rounded-xl px-4 py-3 border border-slate-200">
                    <svg className="w-[18px] h-[18px] text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>
                    <input type="password" placeholder={t('auth_password')} value={password} onChange={e => setPassword(e.target.value)}
                      className="flex-1 bg-transparent outline-none text-sm" autoComplete="new-password" data-testid="auth-password" />
                  </div>
                  {!isLogin && (
                    <div className="flex items-center gap-3 bg-slate-50 rounded-xl px-4 py-3 border border-slate-200">
                      <svg className="w-[18px] h-[18px] text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" /></svg>
                      <input type="tel" placeholder={t('auth_phone')} value={phone} onChange={e => setPhone(e.target.value)}
                        className="flex-1 bg-transparent outline-none text-sm" data-testid="auth-phone" />
                    </div>
                  )}

                  {authError && (
                    <p className="text-sm text-red-500 text-center" data-testid="auth-error">{authError}</p>
                  )}

                  <button type="submit" disabled={authLoading}
                    className="w-full bg-orange-500 hover:bg-orange-600 text-white py-3.5 rounded-xl text-sm font-bold transition-all disabled:opacity-60 mt-2"
                    data-testid="auth-submit">
                    {authLoading ? t('common_loading') : isLogin ? t('auth_login_btn') : t('auth_register_btn')}
                  </button>

                  {isLogin && (
                    <button type="button" onClick={() => { setForgotMode('email'); setAuthError(''); }}
                      className="block mx-auto text-sm text-slate-400 hover:text-orange-500 hover:underline mt-1"
                      data-testid="forgot-password-link">
                      Passwort vergessen?
                    </button>
                  )}
                </form>

                <button onClick={() => { setIsLogin(!isLogin); setAuthError(''); }}
                  className="block mx-auto mt-4 text-sm text-orange-500 hover:underline"
                  data-testid="auth-switch">
                  {isLogin ? t('auth_switch_to_register') : t('auth_switch_to_login')}
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* Mobile Menu */}
      {showMobileMenu && (
        <div className="fixed inset-0 z-50 bg-black/50" onMouseDown={(e) => { if (e.target === e.currentTarget) setShowMobileMenu(false); }} data-testid="mobile-menu-overlay">
          <div className="absolute right-0 top-0 bottom-0 w-72 bg-white shadow-2xl p-6" onMouseDown={e => e.stopPropagation()} data-testid="mobile-menu">
            <button onClick={() => setShowMobileMenu(false)} className="absolute top-4 right-4 text-slate-400 hover:text-slate-600" data-testid="mobile-menu-close">
              <X size={24} />
            </button>

            <div className="mt-10 space-y-2">
              {user ? (
                <div className="flex items-center gap-3 mb-6 pb-6 border-b border-slate-100">
                  <div className="w-10 h-10 rounded-full bg-orange-500 flex items-center justify-center text-white font-bold">
                    {user.name.charAt(0)}
                  </div>
                  <div>
                    <p className="font-semibold text-sm text-slate-800">{user.name}</p>
                    <p className="text-xs text-slate-400">{user.email}</p>
                  </div>
                </div>
              ) : (
                <button onClick={() => { setShowMobileMenu(false); setShowAuthModal(true); }}
                  className="w-full flex items-center justify-center gap-2 bg-orange-500 text-white py-3 rounded-xl text-sm font-semibold mb-6"
                  data-testid="mobile-login-btn">
                  <User size={18} /> Login / Registrieren
                </button>
              )}

              <button onClick={() => { setShowMobileMenu(false); openAuthForDriver(); }} className="flex items-center gap-3 px-4 py-3 rounded-xl bg-slate-50 text-sm font-medium text-slate-700 w-full">
                <Car size={18} className="text-orange-500" /> {t('nav_for_drivers')}
              </button>
              <button onClick={() => { setShowMobileMenu(false); openAuthForCompany(); }} className="flex items-center gap-3 px-4 py-3 rounded-xl bg-slate-50 text-sm font-medium text-slate-700 w-full">
                <Building2 size={18} className="text-orange-500" /> {t('nav_for_companies')}
              </button>
              {user && (
                <a href="/my-transfers" className="flex items-center gap-3 px-4 py-3 rounded-xl bg-slate-50 text-sm font-medium text-slate-700">
                  <Navigation size={18} className="text-orange-500" /> Meine Transfers
                </a>
              )}
              {user?.role === 'driver' && (
                <a href="/driver/dashboard" className="flex items-center gap-3 px-4 py-3 rounded-xl bg-slate-50 text-sm font-medium text-slate-700">
                  <Briefcase size={18} className="text-orange-500" /> Fahrer-Dashboard
                </a>
              )}
              {user?.role === 'admin' && (
                <a href="/admin" className="flex items-center gap-3 px-4 py-3 rounded-xl bg-slate-50 text-sm font-medium text-slate-700">
                  <Shield size={18} className="text-orange-500" /> Admin Dashboard
                </a>
              )}
            </div>

            <div className="mt-8 pt-6 border-t border-slate-100">
              <p className="text-xs text-slate-400 mb-3">Sprache</p>
              <div className="flex gap-2">
                {LANGUAGE_OPTIONS.map(l => (
                  <button key={l.code} onClick={() => setLanguage(l.code)}
                    className={`px-3 py-2 rounded-lg text-xs font-medium transition-colors ${language === l.code ? 'bg-orange-50 text-orange-500 ring-1 ring-orange-500' : 'bg-slate-50 text-slate-500'}`}>
                    {l.name}
                  </button>
                ))}
              </div>
            </div>

            {user && (
              <button onClick={() => { logout(); setShowMobileMenu(false); }}
                className="flex items-center gap-2 text-red-500 text-sm mt-8" data-testid="mobile-logout-btn">
                <LogOut size={16} /> {t('nav_logout')}
              </button>
            )}
          </div>
        </div>
      )}
      {/* Map Picker Modal */}
      {showMapPicker && (
        <Suspense fallback={<div className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center"><Loader2 size={32} className="text-white animate-spin" /></div>}>
          <MapPicker
            onSelect={handleMapSelect}
            onClose={() => setShowMapPicker(null)}
            initialCenter={fromLocation ? [fromLocation.lat, fromLocation.lng] : undefined}
          />
        </Suspense>
      )}
    </div>
  );
}
